import type { Order } from '../types/orders';
import type { Table } from '../types/tables';
import { idbDelete, idbGetAll, idbPut, STORE_ORDERS } from './offlineDb';

/**
 * Cómo van las órdenes que la caja abrió o cobró sin red.
 *
 * Las pantallas (Mesas, Comandear, Cobrar, Órdenes) siguen pidiendo la orden al servidor;
 * cuando no hay servidor, baseApi les entrega esto con la misma forma que la respuesta real,
 * así que ninguna pantalla sabe que está trabajando sin red. Cada snapshot vive hasta que su
 * operación se sincroniza; a partir de ahí manda el servidor.
 */
export interface OfflineOrderSnapshot {
  id: string;
  /**
   * Mesa de la orden. Vacío en para llevar y domicilio: su mesa es la virtual del servidor,
   * que no viene en `/tables` y aquí no se conoce.
   */
  tableId: string;
  /** Contador por dispositivo; en el ticket se imprime como "SIN RED 7". */
  offlineFolio: number;
  status: 'Open' | 'Closed';
  order: Order;
  /**
   * Estado de la mesa que se marcó sin red: precuenta con la orden abierta, o liberada
   * después de cobrarla. Sin él, la mesa se pinta ocupada (abierta) o sucia (cobrada).
   */
  tableStatus?: 'PreCheck' | 'Available';
  /**
   * Restaurante en que se tomó. La lista de Órdenes sin red solo muestra las del restaurante
   * actual: con RestaurantSwitcher, las de otro se cobraban aquí y el servidor las rechazaba
   * al sincronizar. (Las de F2b no lo traen; se muestran siempre.)
   */
  restaurantId?: string;
}

const FOLIO_KEY = 'offlineFolioSeq';

/**
 * Folio provisional. No se parece a un folio real a propósito: el ticket dice
 * "SIN RED 7" y "Folio pendiente de sincronizar", para que nadie lo tome por el
 * consecutivo de la orden, que lo asigna el servidor al sincronizar.
 */
export const nextOfflineFolio = (): number => {
  try {
    const next = (Number(localStorage.getItem(FOLIO_KEY)) || 0) + 1;
    localStorage.setItem(FOLIO_KEY, String(next));
    return next;
  } catch {
    return 1;
  }
};

export const formatOfflineFolio = (folio: number): string => `SIN RED ${folio}`;

export async function listOfflineOrders(): Promise<OfflineOrderSnapshot[]> {
  try {
    return await idbGetAll<OfflineOrderSnapshot>(STORE_ORDERS);
  } catch {
    return [];
  }
}

export async function getOfflineOrder(orderId: string): Promise<OfflineOrderSnapshot | undefined> {
  return (await listOfflineOrders()).find((o) => o.id === orderId);
}

export async function getOfflineOrderByTable(tableId: string): Promise<OfflineOrderSnapshot | undefined> {
  return (await listOfflineOrders()).find((o) => o.tableId === tableId && o.status === 'Open');
}

/**
 * La orden que decide cómo se ve cada mesa: la abierta si hay una; si no, la última que se
 * cobró. Una mesa puede tener varias sin red (se cobró, se liberó y se volvió a abrir), y
 * tomar cualquiera la pintaba sucia con una orden viva encima.
 */
export function currentOfflineOrderByTable(snapshots: OfflineOrderSnapshot[]): Map<string, OfflineOrderSnapshot> {
  const byTable = new Map<string, OfflineOrderSnapshot>();
  for (const snapshot of snapshots) {
    if (!snapshot.tableId) continue;
    const current = byTable.get(snapshot.tableId);
    if (!current || rank(snapshot) > rank(current)) {
      byTable.set(snapshot.tableId, snapshot);
    }
  }
  return byTable;
}

const rank = (s: OfflineOrderSnapshot): string =>
  `${s.status === 'Open' ? 1 : 0}|${s.order.closeTime ?? s.order.openTime ?? ''}`;

export const saveOfflineOrder = (snapshot: OfflineOrderSnapshot): Promise<unknown> =>
  idbPut(STORE_ORDERS, snapshot);

export const removeOfflineOrder = (orderId: string): Promise<unknown> =>
  idbDelete(STORE_ORDERS, orderId);

/**
 * Pinta en el mapa de mesas lo que pasó sin red: la mesa que se abrió aparece ocupada (o en
 * precuenta), la que se cobró aparece sucia (o libre, si se liberó), igual que si el servidor
 * lo supiera. Sin esto, la copia guardada mostraría libre una mesa que la cajera acaba de
 * ocupar.
 */
export function mergeOfflineTables(tables: Table[], snapshots: OfflineOrderSnapshot[]): Table[] {
  if (snapshots.length === 0) return tables;
  const byTable = currentOfflineOrderByTable(snapshots);

  return tables.map((table) => {
    const snapshot = byTable.get(table.id);
    if (!snapshot) return table;
    return { ...table, ...offlineTableState(snapshot, table) };
  });
}

/** Lo que cambia de una mesa según su orden sin red. */
export function offlineTableState(snapshot: OfflineOrderSnapshot, table: Table): Partial<Table> {
  if (snapshot.status === 'Closed') {
    return {
      status: snapshot.tableStatus === 'Available' ? 'Available' : 'Dirty',
      currentOrderId: undefined,
      currentOrderTotal: undefined,
    };
  }

  return {
    status: snapshot.tableStatus === 'PreCheck' ? 'PreCheck' : 'Occupied',
    currentOrderId: snapshot.id,
    currentOrderTotal: snapshot.order.total,
    guestCount: snapshot.order.adults ?? table.guestCount,
  };
}
